// City picker modal — used by the host event create form.
// Returns the full city object on select so the caller can render
// emoji + name without a second fetch.

import { useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { useSearchCities } from "../lib/api/useCities";
import { SheetModal } from "./SheetModal";

export type SelectedCity = {
  id: string;
  name: string;
  emoji: string;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  selectedId?: string;
  onSelect: (city: SelectedCity) => void;
  // Hide the "Coming soon" badge — used on the public events browse surface,
  // where the badge (about Outclose dinner availability) is irrelevant.
  hideComingSoon?: boolean;
}

export function CitySelectModal({ isOpen, onClose, selectedId, onSelect, hideComingSoon }: Props) {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");

  // Reset search whenever the modal reopens.
  useEffect(() => {
    if (isOpen) setSearch("");
  }, [isOpen]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useSearchCities(debounced, selectedId, 50, false);
  const cities = data?.success ? data.data ?? [] : [];

  return (
    <SheetModal isOpen={isOpen} onClose={onClose} className="sm:max-w-lg max-h-[80vh]">
      {/* Header */}
      <div className="px-5 pt-5 pb-4 flex items-center gap-3 border-b border-neutral-200 dark:border-neutral-800 shrink-0">
        <h2 className="text-lg font-semibold flex-1">Choose a city</h2>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-neutral-500"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Search */}
      <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search cities…"
            className="w-full pl-9 pr-4 py-2 rounded-xl bg-neutral-100 dark:bg-neutral-800 border-0 outline-none text-sm placeholder:text-neutral-400 focus:ring-2 focus:ring-primary-500/40"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {isLoading && cities.length === 0 && (
          <div className="text-center text-sm text-neutral-500 dark:text-neutral-400 py-8">
            Searching…
          </div>
        )}
        {!isLoading && cities.length === 0 && (
          <div className="text-center text-sm text-neutral-500 dark:text-neutral-400 py-8">
            No cities match "{search}"
          </div>
        )}
        {cities.map((c) => {
          const isSelected = c.id === selectedId;
          return (
            <button
              type="button"
              key={c.id}
              onClick={() => {
                onSelect({ id: c.id, name: c.name, emoji: c.emoji });
                onClose();
              }}
              className={`
                w-full text-left px-4 py-3 rounded-xl flex items-center gap-3
                transition-colors
                ${isSelected ? "bg-primary-500/10 text-primary-500" : "hover:bg-neutral-100 dark:hover:bg-neutral-800"}
              `}
            >
              <span className="text-xl shrink-0">{c.emoji}</span>
              <span className="flex-1 font-medium">{c.name}</span>
              {!hideComingSoon && !c.available && (
                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                  Coming soon
                </span>
              )}
            </button>
          );
        })}
      </div>
    </SheetModal>
  );
}
